import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { ContentCard, Empty } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $remoteBookingLoads } from '../../_state/bookingDetail.state';

import { BookingTabDocumentsTableHeader } from './BookingTabDocumentsTableHeader';
import { BookingTabDocumentsTableRow } from './BookingTabDocumentsTableRow';
import { useCheckboxes } from './hooks/useCheckboxes';

import { FloatingActionBar } from '~/modules/trade/_components/_shared/FloatingActionBar';
import { useGenerateDocumentsAndSendEmail } from '~/modules/trade/hooks/useGenerateDocumentsAndSendEmail';
import { usePrintWithBrowser } from '~/modules/trade/hooks/usePrintWithBrowser';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { BookingRecord } from '~/modules/trade/types/booking';

interface BookingDocumentsTabProps {
  booking: BookingRecord;
}

const DOCUMENT_DISPLAY_NAMES: Partial<Record<DocumentType, string>> = {
  Invoice: 'Invoice',
  PackingList: 'Packing List',
  BillOfLading: 'Bill of Lading',
  SalesOrder: 'Sales Order',
  PurchaseOrder: 'Purchase Order',
  ShippingSummary: 'Shipping Summary',
  BookingInvoice: 'Booking Invoice',
  LoadInvoice: 'Load Invoice',
};

export const BookingDocumentsTab: React.FC<BookingDocumentsTabProps> = ({
  booking,
}) => {
  const t = useTranslations();

  // A map where the key is the ID of the load
  // And the value is a map where the key is the type of document, and the value is a boolean indicating whether the document is selected
  const [selectedDocumentsByLoad, setSelectedDocumentsByLoad] = React.useState<
    Record<string, Record<DocumentType, boolean>>
  >({});
  const [isActionBarOpen, setIsActionBarOpen] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Fetch all loads for this booking
  const { data: loadsData, isLoading } = useAtomValue($remoteBookingLoads);
  const loads = loadsData?.loads ?? [];

  const { handleLoadSelect, handleDocumentSelect, handleHeaderSelect } =
    useCheckboxes(loads);

  const selectedLoadDocuments = Object.values(selectedDocumentsByLoad).reduce(
    (acc, loadDocs) => acc + Object.values(loadDocs).filter(Boolean).length,
    0,
  );

  const selectedCount = selectedLoadDocuments;

  // Hooks for printing and emailing
  const { print, isPrinting } = usePrintWithBrowser();
  const { sendDocumentsAndEmail, isPending } =
    useGenerateDocumentsAndSendEmail();

  React.useEffect(() => {
    setIsActionBarOpen(selectedCount > 0);
  }, [selectedCount]);

  const handleCloseActionBar = () => {
    setIsActionBarOpen(false);
    setSelectedDocumentsByLoad({});
  };

  const handlePrintDocuments = React.useCallback(async () => {
    if (selectedLoadDocuments === 0) {
      return;
    }

    // Convert selected documents to the format expected by the print hook
    const documentsToPrint = Object.entries(selectedDocumentsByLoad).flatMap(
      ([loadId, loadDocs]) =>
        Object.entries(loadDocs)
          .filter(([_, checked]) => checked)
          .map(([documentType]) => {
            // Find the load to get the invoiceId
            const load = loadsData?.loads?.find((l) => l.id === loadId);
            return {
              documentType: documentType as DocumentType,
              loadId,
              invoiceId:
                documentType === 'Invoice'
                  ? (load?.invoiceId ?? undefined)
                  : undefined,
            };
          }),
    );

    if (documentsToPrint.length === 0) {
      return;
    }

    try {
      await print({
        documents: documentsToPrint,
      });

      // Clear selection after successful print
      setSelectedDocumentsByLoad({});
    } catch (error) {
      console.error('Failed to print documents:', error);
    }
  }, [selectedDocumentsByLoad, selectedLoadDocuments, print, loadsData?.loads]);

  const handleDownloadDocuments = React.useCallback(async () => {
    if (selectedLoadDocuments === 0) {
      return;
    }

    setIsDownloading(true);
    try {
      // Convert selected documents to the format expected by the print hook
      const documentsToDownload = Object.entries(
        selectedDocumentsByLoad,
      ).flatMap(([loadId, loadDocs]) =>
        Object.entries(loadDocs)
          .filter(([_, checked]) => checked)
          .map(([documentType]) => {
            // Find the load to get the invoiceId
            const load = loadsData?.loads?.find((l) => l.id === loadId);
            return {
              documentType: documentType as DocumentType,
              loadId,
              invoiceId:
                documentType === 'Invoice'
                  ? (load?.invoiceId ?? undefined)
                  : undefined,
            };
          }),
      );

      if (documentsToDownload.length === 0) {
        return;
      }

      await print({
        documents: documentsToDownload,
        action: 'download',
      });

      // Clear selection after successful download
      setSelectedDocumentsByLoad({});
    } catch (error) {
      console.error('Failed to download documents:', error);
    } finally {
      setIsDownloading(false);
    }
  }, [selectedDocumentsByLoad, selectedLoadDocuments, print, loadsData?.loads]);

  const handleSendEmail = React.useCallback(
    async (emails: string[]) => {
      if (emails.length === 0) {
        return;
      }

      // Convert selected documents to the format expected by the email hook
      const documentsToEmail = Object.entries(selectedDocumentsByLoad).flatMap(
        ([loadId, loadDocs]) =>
          Object.entries(loadDocs)
            .filter(([_, checked]) => checked)
            .map(([documentType]) => ({
              documentType: documentType as DocumentType,
              loadId,
            })),
      );

      if (documentsToEmail.length === 0) {
        return;
      }

      try {
        await sendDocumentsAndEmail({
          documents: documentsToEmail,
          email: {
            to: emails,
            subject: t.bookingDetail.documents.email.subject(
              booking.bookingNumber,
            ),
            textBody: t.bookingDetail.documents.email.textBody({
              bookingNumber: booking.bookingNumber,
              documentTypes: documentsToEmail
                .map((doc) => DOCUMENT_DISPLAY_NAMES[doc.documentType])
                .join(', '),
            }),
            htmlBody: t.bookingDetail.documents.email.htmlBody({
              bookingNumber: booking.bookingNumber,
              documentTypes: documentsToEmail
                .map((doc) => DOCUMENT_DISPLAY_NAMES[doc.documentType])
                .join(', '),
            }),
          },
        });

        // Clear selection after successful email send
        setSelectedDocumentsByLoad({});
      } catch (error) {
        // Error handling is done in the hook
        console.error('Failed to send documents via email:', error);
      }
    },
    [selectedDocumentsByLoad, sendDocumentsAndEmail, booking.bookingNumber, t],
  );

  const handlePostPrint = React.useCallback(() => {
    setSelectedDocumentsByLoad({});
  }, []);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
          <div className="h-24 rounded bg-neutral/20"></div>
          <div className="h-24 rounded bg-neutral/20"></div>
        </div>
      </div>
    );
  }

  return loads.length > 0 ? (
    <div className="@BookingDocuments p-5">
      <ContentCard className="w-full">
        {/* Load Documents Section */}
        <div className="w-full bg-white">
          <div className="w-full">
            <BookingTabDocumentsTableHeader
              selectedDocuments={selectedDocumentsByLoad}
              loads={loads}
              onHeaderSelect={(type, checked) =>
                setSelectedDocumentsByLoad(
                  handleHeaderSelect(type, checked)(selectedDocumentsByLoad),
                )
              }
            />
            {loads?.map((load) => (
              <BookingTabDocumentsTableRow
                key={load.id}
                load={load}
                selectedDocuments={selectedDocumentsByLoad}
                onLoadSelect={(loadId, checked) =>
                  setSelectedDocumentsByLoad(
                    handleLoadSelect(loadId, checked)(selectedDocumentsByLoad),
                  )
                }
                onDocumentSelect={(loadId, type, checked) =>
                  setSelectedDocumentsByLoad(
                    handleDocumentSelect(
                      loadId,
                      type,
                      checked,
                    )(selectedDocumentsByLoad),
                  )
                }
              />
            ))}
          </div>
        </div>
        {/* Floating Action Bar */}
        {selectedCount > 0 && (
          <div className="pointer-events-none absolute bottom-0 left-0 flex w-full justify-center">
            <div className="pointer-events-auto">
              <FloatingActionBar
                selectedCount={selectedCount}
                isOpen={isActionBarOpen}
                onClose={handleCloseActionBar}
                onPrint={handlePrintDocuments}
                onDownload={handleDownloadDocuments}
                onSendEmail={handleSendEmail}
                onPostPrint={handlePostPrint}
                isPrinting={isPrinting}
                isDownloading={isDownloading}
                isEmailSending={isPending}
              />
            </div>
          </div>
        )}
      </ContentCard>
    </div>
  ) : (
    <div className="flex h-full items-center justify-center">
      <Empty
        className="h-full"
        title={t.bookingDetail.documents.noDocuments}
        centered={true}
      />
    </div>
  );
};
